import { useEffect, useState } from 'react';
import EmojiFlags from 'emoji-flags';
import cn from 'classnames';
import ContactInfo from '../../shared/contactInfo';
import styles from './localisedContact.module.scss';
import ChevronUp from '../../shared/chevron-up';
import ChevronDown from '../../shared/chevron-down';
import { checkIsWindowsOS } from '../../../lib/utils/helpers';

const LocalisedContact = ({ data, locale }) => {
  const { contactsList, continentsList, title, subtitle, anchorId } = data;
  const [selectedTab, setSelectedTab] = useState(0); // 0 is HQ
  const [selectedCountry, setSelectedCountry] = useState(
    contactsList?.find(({ continentName }) => continentName === 'HQ') // show first country from HQ
  );
  const [activeDropdown, setActiveDropdown] = useState(false);
  const [isWindows, setIsWindows] = useState('');

  useEffect(() => {
    setIsWindows(checkIsWindowsOS());
  }, []);

  // do not show international contacts on included locales e.g. /nl, /fr, /de
  if (locale !== 'en') return null;

  const continentTabs = continentsList.map((title, index) => ({
    title,
    index,
  }));

  // for dropdown
  const countriesList = contactsList
    .filter(
      contact =>
        contact.continentName === continentTabs.find(tab => tab.index === selectedTab)?.title
    )
    .map(contact => {
      const details = EmojiFlags[contact.locale.toUpperCase()];
      const label = `${details.emoji} ${details.name}`;

      return {
        value: contact.locale.toUpperCase(),
        label,
      };
    });

  const showSelectedTab = tabTitle =>
    contactsList
      .filter(({ continentName }) => continentName === tabTitle)
      .map(contact => (
        <ContactInfo data={contact} hideContact={contact.locale !== selectedCountry.locale} />
      ));

  const handleTabClick = tab => {
    setSelectedTab(tab.index);

    // selecting the first country of the continent in order to show some contact information
    setSelectedCountry(contactsList.find(contact => contact.continentName === tab.title));
  };

  return (
    <section id={anchorId} className={cn(styles.containerSection)}>
      <div className={styles.contactHeader}>
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subtitle}>{subtitle}</h3>
      </div>
      <div className={cn(styles.tabs)}>
        <div className={styles.tabHeader}>
          <div className={styles.tabButtons}>
            {continentTabs.map(tab => (
              <div
                className={cn(styles.tabButton, { [styles.selected]: tab.index === selectedTab })}
                onClick={() => handleTabClick(tab)}
                onKeyDown={() => handleTabClick(tab)}
                tabIndex={tab.index}
                role="button"
              >
                {tab.title}
              </div>
            ))}
          </div>
        </div>
        {countriesList && countriesList.length && (
          <div
            className={styles.selectedCountryContainer}
            onClick={() => setActiveDropdown(!activeDropdown)}
            role="none"
          >
            <button type="button" className={cn(styles.button, styles.resetMargin)}>
              <>
                {countriesList
                  .find(c => c.value === selectedCountry.locale.toUpperCase())
                  ?.label?.replace(' ', '/')
                  ?.split('/')
                  .map((label, index) => (
                    <span
                      className={cn({
                        [styles.countryCodeEmoji]: !index && !isWindows,
                        [styles.hidden]: !index && isWindows,
                      })}
                    >
                      {label}
                    </span>
                  ))}
              </>
            </button>
            <div className={styles.chevronContainer}>
              {activeDropdown ? (
                <ChevronUp width="1.8rem" height="1.8rem" />
              ) : (
                <ChevronDown width="1.8rem" height="1.8rem" />
              )}
            </div>
            {activeDropdown && (
              <ul className={styles.countriesListContainer}>
                {countriesList
                  .filter(c => c.value.toLowerCase() !== selectedCountry?.locale)
                  .map(country => (
                    <li
                      role="none"
                      className={styles.countryItem}
                      value={country?.countryCode}
                      onClick={() => {
                        setSelectedCountry(
                          contactsList.find(
                            contact => contact.locale === country.value.toLowerCase()
                          )
                        );
                        setActiveDropdown(!activeDropdown);
                      }}
                    >
                      {country?.label
                        ?.replace(' ', '/')
                        ?.split('/')
                        ?.map((label, index) => (
                          <span
                            className={cn({
                              [styles.countryCodeEmoji]: !index && !isWindows,
                              [styles.hidden]: !index && isWindows,
                            })}
                          >
                            {label}
                          </span>
                        ))}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
        {continentTabs.map(tab => (
          <div className={cn(styles.tabContent, { [styles.visible]: tab.index === selectedTab })}>
            {showSelectedTab(tab.title)}
          </div>
        ))}
      </div>
    </section>
  );
};

export default LocalisedContact;
